import React, { useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Switch } from 'app/components/ui/switch';
import { Separator } from 'app/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'app/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from 'app/components/ui/form';
import { Eye, EyeOff } from 'lucide-react';
import { useToast } from 'app/components/ui/use-toast';
import { useEmailConfigSlice } from '../../slice';

// Zod schema for form validation
const brevoConfigSchema = z.object({
  sender_name: z.string().min(1, 'Sender name is required'),
  sender_email: z.string().email('Invalid email address'),
  api_key: z.string().min(1, 'API key is required'),
  is_active: z.boolean().default(false),
});

type BrevoConfigFormData = z.infer<typeof brevoConfigSchema>;

interface BrevoConfigData {
  id?: string;
  sender_name: string;
  sender_email: string;
  api_key: string;
  is_active: number;
  created_at?: string;
  updated_at?: string;
}

interface BrevoPopupProps {
  isOpen: boolean;
  onClose: () => void;
  config?: BrevoConfigData | null;
}

const BrevoPopup: React.FC<BrevoPopupProps> = ({ isOpen, onClose, config }) => {
  const { toast } = useToast();
  const [showApiKey, setShowApiKey] = useState(false);
  const { useUpdateBrevoConfigMutation, useAddBrevoConfigMutation } =
    useEmailConfigSlice();

  const [
    updateConfig,
    { isLoading: isUpdating, error: updateError, isSuccess: updateSuccess },
  ] = useUpdateBrevoConfigMutation();
  const [
    addConfig,
    { isLoading: isAdding, error: addError, isSuccess: addSuccess },
  ] = useAddBrevoConfigMutation();

  // Initialize form with React Hook Form
  const form = useForm<BrevoConfigFormData>({
    resolver: zodResolver(brevoConfigSchema),
    defaultValues: {
      sender_name: '',
      sender_email: '',
      api_key: '',
      is_active: false,
    },
  });

  // Update form values when config changes
  useEffect(() => {
    if (config) {
      form.reset({
        sender_name: config.sender_name || '',
        sender_email: config.sender_email || '',
        api_key: config.api_key || '',
        is_active: config.is_active === 1,
      });
    } else {
      form.reset({
        sender_name: '',
        sender_email: '',
        api_key: '',
        is_active: false,
      });
    }
  }, [config, form]);

  // Handle form submission
  const onSubmit = async (data: BrevoConfigFormData) => {
    try {
      const formData = {
        senderName: data.sender_name,
        senderEmail: data.sender_email,
        apiKey: data.api_key,
        isActive: data.is_active,
      };

      if (config?.id) {
        // Update existing config
        await updateConfig({ id: config.id, data: formData }).unwrap();
      } else {
        // Add new config
        await addConfig(formData).unwrap();
      }

      toast({
        title: config ? 'Configuration Updated' : 'Configuration Added',
        description: config
          ? 'Brevo configuration has been updated successfully.'
          : 'Brevo configuration has been added successfully.',
        variant: 'success',
      });

      handleClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save configuration. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    form.reset();
    setShowApiKey(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto rounded-xl">
        <DialogHeader className="bg-white py-3">
          <DialogTitle className="flex items-center gap-2 border-b border-gray-200 pb-4">
            <div className="mr-5">
              <img
                src="/images/brevo.svg"
                alt="Brevo"
                className="max-w-[85px] h-auto"
              />
            </div>
            <div className="text-xl font-semibold text-gray-800">
              Brevo Configuration
            </div>
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 px-5 pb-5"
          >
            {/* Basic Configuration */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Basic Configuration</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="sender_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sender Name *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Shadowgrow"
                          {...field}
                          autoComplete="off"
                        />
                      </FormControl>
                      <FormDescription>
                        This is the name that will be used to send emails.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sender_email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sender Email *</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="shadowgrow@gmail.com"
                          {...field}
                          autoComplete="off"
                        />
                      </FormControl>
                      <FormDescription>
                        This is the email address that will be used to send
                        emails.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* API Configuration */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">API Configuration</h3>

              <FormField
                control={form.control}
                name="api_key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>API Key *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showApiKey ? 'text' : 'password'}
                          placeholder="30-l5czCRksWqu9q3np"
                          {...field}
                          autoComplete="off"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowApiKey(!showApiKey)}
                        >
                          {showApiKey ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormDescription>
                      Get your API key from your Brevo dashboard
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Status */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Active Provider
                      </FormLabel>
                      <FormDescription>
                        Only active providers will be used for sending emails
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdating || isAdding}>
                {isUpdating || isAdding
                  ? 'Saving...'
                  : config
                  ? 'Update Configuration'
                  : 'Activate Configuration'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default BrevoPopup;
